import { useEffect, useState } from 'react';
import { Button as AriaButton, Disclosure, DisclosurePanel, Heading } from 'react-aria-components';
import { useParams } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getStaffTransportReservations } from '@/api/getStaffTransportReservations';
import BoardingInfo from '@/components/Staff/BoardingInfo';
import GetOffInfo from '@/components/Staff/GetOffInfo';
import { LineUserAtom } from '@/jotai/jotai';
import { formattedDate } from '@/logics/date/date';
import { StaffTransportReservation } from '@/types/pickupDropoff';

export const StaffTransportPage = () => {
  const [lineUser, _] = useAtom(LineUserAtom);
  const [displayDate, setDisplayDate] = useState<string>();
  const [reservations, setReservations] = useState<StaffTransportReservation[]>([]);
  const { office_id } = useParams();
  // const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    setDisplayDate(formattedDate(new Date(), 'yyyy年M月dd日(EEE)'));
    if (office_id && lineUser) {
      fetchReservations();
    }
  }, [lineUser]);

  // **車両ごとに予約を分類（NULL の場合は "未設定"）**
  const vehicleReservations: Record<
    string,
    {
      vehicleName: string;
      pickupReservations: StaffTransportReservation[];
      dropoffReservations: StaffTransportReservation[];
    }
  > = {};

  reservations.forEach((res) => {
    const vehicleId = res.pickup_transport_info?.vehicle_id ?? 0;
    const vehicleName = res.pickup_transport_info?.vehicle_name ?? '車両未定';
    const vehicleKey = vehicleId ? `vehicle_${vehicleId}` : '車両未定';
    if (!vehicleReservations[vehicleKey]) {
      vehicleReservations[vehicleKey] = {
        vehicleName,
        pickupReservations: [],
        dropoffReservations: [],
      };
    }
    // 来所予約 (pickup)
    if (res.pickup_transport_info?.vehicle_id === vehicleId) {
      vehicleReservations[vehicleKey].pickupReservations.push(res);
    }
    // 退所予約 (dropoff)
    if (res.dropoff_transport_info?.vehicle_id === vehicleId) {
      vehicleReservations[vehicleKey].dropoffReservations.push(res);
    }
  });

  const fetchReservations = async () => {
    const result = await getStaffTransportReservations(
      lineUser?.lineID ?? '',
      lineUser?.accessToken ?? '',
      office_id ?? '0'
    );
    if (result.status === 200) {
      console.log('取得した予約情報:', result.result);
      setReservations(result.result);
    }
  };

  return (
    <div className="m-4 space-y-4">
      <Heading className="text-2xl font-bold">送迎予約一覧</Heading>

      {/* 予約がない場合 */}
      {Object.keys(vehicleReservations).length === 0 && (
        <div className="text-lg">本日の予約はありません</div>
      )}

      {/* 各車両ごとにグループ化 */}
      {Object.entries(vehicleReservations).map(
        ([vehicleKey, { vehicleName, pickupReservations, dropoffReservations }]) => (
          <div
            key={vehicleKey}
            className="space-y-4"
          >
            {/* 日付と車両名を青い枠の中に表示 */}
            <div className="rounded-8 bg-blue-100 px-2 py-3">
              <Heading className="text-lg font-bold">{displayDate}</Heading>
              <Heading className="text-lg font-bold">{`${vehicleName}`}</Heading>
            </div>

            {/* 来所予約 */}
            {pickupReservations.length > 0 && (
              <Disclosure className="h-full rounded-md bg-gray-100">
                <Heading>
                  <AriaButton
                    className="flex w-full flex-row items-center justify-between p-4 py-4"
                    slot="trigger"
                  >
                    <div className="text-lg font-bold">送迎（来所）</div>
                  </AriaButton>
                </Heading>
                <DisclosurePanel>
                  <BoardingInfo
                    officeID={office_id}
                    reservations={pickupReservations}
                    refreshReservations={fetchReservations}
                  />
                </DisclosurePanel>
              </Disclosure>
            )}

            {/* 退所予約 */}
            {dropoffReservations.length > 0 && (
              <Disclosure className="h-full rounded-md bg-gray-100">
                <Heading>
                  <AriaButton
                    className="flex w-full flex-row items-center justify-between p-4 py-4"
                    slot="trigger"
                  >
                    <div className="text-lg font-bold">送迎（退所）</div>
                  </AriaButton>
                </Heading>
                <DisclosurePanel>
                  <GetOffInfo
                    officeID={office_id}
                    reservations={dropoffReservations}
                    refreshReservations={fetchReservations}
                  />
                </DisclosurePanel>
              </Disclosure>
            )}
          </div>
        )
      )}
    </div>
  );
};
